import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';

const EVENT_TYPES = [
  { value: 'staff_party', label: 'Staff Party' },
  { value: 'team_building', label: 'Team Building' },
  { value: 'trade_show', label: 'Trade Show / Exhibition' },
  { value: 'brand_activation', label: 'Brand Activation' },
  { value: 'other', label: 'Other' },
];

// Shared lead-capture form for both Corporate (B2B) and Party (B2C) group
// booking enquiries. Creates a GroupBookingEnquiry record tagged with the
// given enquiry_type. Corporate shows company/event fields; Party shows
// party-type/driver fields.
export default function GroupEnquiryForm({ type }) {
  const isCorporate = type === 'corporate';
  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    event_date: '',
    number_of_attendees: '',
    event_type: 'staff_party',
    party_type: 'kids',
    number_of_drivers: '',
    preferred_date: '',
    special_requests: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim() || !form.contact_email.trim()) {
      setError('Please provide your name and email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        enquiry_type: type,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim().toLowerCase(),
        contact_phone: form.contact_phone.trim() || undefined,
        special_requests: form.special_requests.trim() || undefined,
      };
      if (isCorporate) {
        payload.company_name = form.company_name.trim() || undefined;
        payload.event_date = form.event_date || undefined;
        payload.number_of_attendees = form.number_of_attendees ? Number(form.number_of_attendees) : undefined;
        payload.event_type = form.event_type;
      } else {
        payload.party_type = form.party_type;
        payload.number_of_drivers = form.number_of_drivers ? Number(form.number_of_drivers) : undefined;
        payload.preferred_date = form.preferred_date || undefined;
      }
      const res = await base44.functions.invoke('createGroupBookingEnquiry', payload);
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || 'Something went wrong. Please try again or call us on 0151 528 3001.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || err?.response?.data?.error || 'Something went wrong. Please try again or call us on 0151 528 3001.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const firstName = form.contact_name.trim().split(' ')[0];
    return (
      <div className="text-center py-16 px-6 border border-primary/20 bg-primary/5">
        <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="text-white font-black text-xl uppercase mb-2 tracking-wide">Enquiry Received</h3>
        <p className="text-white/50 text-sm max-w-md mx-auto leading-relaxed">
          Thanks{firstName ? `, ${firstName}` : ''}! Our team will be in touch within 24 hours to discuss your{' '}
          {isCorporate ? 'corporate event' : 'party'} requirements.
        </p>
        <p className="text-white/30 text-xs mt-4">Reference will be included in our follow-up email.</p>
      </div>
    );
  }

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1.5';
  const inputClass = 'bg-[#111115] border-white/10 text-white h-11 focus:border-primary/60';

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>CONTACT NAME *</label>
          <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="Your full name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>EMAIL *</label>
          <Input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="you@email.com" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>PHONE</label>
          <Input type="tel" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="07xxx xxx xxx" className={inputClass} />
        </div>
        {isCorporate ? (
          <div>
            <label className={labelClass}>COMPANY NAME</label>
            <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Company / organisation" className={inputClass} />
          </div>
        ) : (
          <div>
            <label className={labelClass}>TYPE OF PARTY</label>
            <select
              value={form.party_type}
              onChange={(e) => set('party_type', e.target.value)}
              className={`seo-input h-11 ${inputClass}`}
            >
              <option value="kids">Kids (Ages 8–15)</option>
              <option value="adults">Adults (16+)</option>
            </select>
          </div>
        )}
      </div>

      {isCorporate ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>EVENT DATE</label>
            <Input type="date" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>ATTENDEES</label>
            <Input type="number" min="1" value={form.number_of_attendees} onChange={(e) => set('number_of_attendees', e.target.value)} placeholder="e.g. 30" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>EVENT TYPE</label>
            <select
              value={form.event_type}
              onChange={(e) => set('event_type', e.target.value)}
              className={`seo-input h-11 ${inputClass}`}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>PREFERRED DATE</label>
            <Input type="date" value={form.preferred_date} onChange={(e) => set('preferred_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>EXPECTED DRIVERS</label>
            <Input type="number" min="1" value={form.number_of_drivers} onChange={(e) => set('number_of_drivers', e.target.value)} placeholder="e.g. 8" className={inputClass} />
          </div>
        </div>
      )}

      <div>
        <label className={labelClass}>SPECIAL REQUESTS</label>
        <textarea
          value={form.special_requests}
          onChange={(e) => set('special_requests', e.target.value)}
          rows={3}
          placeholder={isCorporate ? 'Tell us about your event goals, catering needs, branding requirements...' : 'Birthday child\'s name, dietary needs, accessibility, specific games...'}
          className={`seo-input h-auto py-3 ${inputClass}`}
        />
      </div>

      {error && (
        <p className="text-red-400 text-xs font-medium">{error}</p>
      )}

      <div className="flex justify-center">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {isCorporate ? 'REQUEST CORPORATE QUOTE' : 'CHECK AVAILABILITY'}
        </button>
      </div>
    </form>
  );
}